/**
 * songStorage.js
 * Browser-side IndexedDB persistent storage for heavy song data (audio Blobs and synced lyrics).
 * No server-side audio disk storage required for user-added custom songs.
 */

const DB_NAME = "freeflash_songs_db";
const DB_VERSION = 1;
const STORE_NAME = "songs";

let dbPromise = null;

function openDB() {
    if (dbPromise) return dbPromise;

    dbPromise = new Promise((resolve, reject) => {
        if (!window.indexedDB) {
            console.error("IndexedDB is not supported in this browser.");
            return reject(new Error("IndexedDB not supported"));
        }

        const request = window.indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: "id" });
            }
        };

        request.onsuccess = (event) => {
            resolve(event.target.result);
        };

        request.onerror = (event) => {
            console.error("Failed to open IndexedDB:", event.target.error);
            reject(event.target.error);
        };
    });

    return dbPromise;
}

/**
 * Save audio blob and lyrics data locally into IndexedDB.
 */
export async function saveLocalSong(songId, audioBlob, lyricsData) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);

        const record = {
            id: String(songId),
            audioBlob: audioBlob,
            lyricsData: lyricsData,
            audioSizeBytes: audioBlob ? audioBlob.size : 0,
            savedAt: Date.now()
        };

        const req = store.put(record);
        req.onsuccess = () => resolve(true);
        req.onerror = (err) => reject(err);
    });
}

/**
 * Retrieve local song data and audio blob by songId.
 */
export async function getLocalSong(songId) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const req = store.get(String(songId));

        req.onsuccess = () => {
            const result = req.result;
            if (!result) return resolve(null);

            let audioUrl = null;
            if (result.audioBlob) {
                audioUrl = URL.createObjectURL(result.audioBlob);
            }

            resolve({
                id: result.id,
                audioBlob: result.audioBlob,
                audioUrl: audioUrl,
                lyricsData: result.lyricsData,
                savedAt: result.savedAt,
                audioSizeBytes: result.audioSizeBytes || 0
            });
        };

        req.onerror = (err) => reject(err);
    });
}

/**
 * Check if audio for songId exists in this browser's IndexedDB.
 */
export async function hasLocalSong(songId) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const req = store.get(String(songId));

        req.onsuccess = () => {
            const hasData = Boolean(req.result && req.result.audioBlob);
            resolve(hasData);
        };

        req.onerror = () => resolve(false);
    });
}

/**
 * Delete a song from local IndexedDB.
 */
export async function deleteLocalSong(songId) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        const req = store.delete(String(songId));

        req.onsuccess = () => resolve(true);
        req.onerror = (err) => reject(err);
    });
}

/**
 * Return all locally cached song IDs.
 */
export async function getAllLocalSongIds() {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const req = store.getAllKeys();

        req.onsuccess = () => resolve(req.result || []);
        req.onerror = (err) => reject(err);
    });
}

/**
 * Calculate total local storage footprint in MBs and song count.
 */
export async function getStorageFootprint() {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const req = store.openCursor();

        let totalBytes = 0;
        let count = 0;

        req.onsuccess = (e) => {
            const cursor = e.target.result;
            if (cursor) {
                count++;
                const val = cursor.value;
                if (val && val.audioSizeBytes) {
                    totalBytes += val.audioSizeBytes;
                } else if (val && val.audioBlob && val.audioBlob.size) {
                    totalBytes += val.audioBlob.size;
                }
                cursor.continue();
            } else {
                const totalMB = (totalBytes / (1024 * 1024)).toFixed(1);
                resolve({ count, totalBytes, totalMB });
            }
        };

        req.onerror = (err) => reject(err);
    });
}
